import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  If,
} from '@paytrace/pt-skel/Component'
import Event from '../Event'
import PubSub from '@paytrace/pt-skel/Event/PubSub'
import { useMountUnmount } from '@paytrace/pt-skel/Hooks'
import { downloadFile } from '@paytrace/pt-skel/Helper/File'
import { TransactionHistorySearch as TransactionHistorySearchReq } from '@paytrace/pt-sdk'
import { formatter } from '../Helper/Money'
import { GridIcon } from './Icons'
import Accordion from './Accordion'
import { TransactionTable } from './ViewTransactionTransactionTable'
import { CardSummaryTable } from './ViewTransactionSummaryTable'
import { Pagination } from './ViewTransactionResultPagination'
import { TransactionSectionControls } from './TransactionSectionControls'
import {
  getPagedTransactions, getSortedTransactions, SortConfig,
} from '../Helper/ViewTransactions'
import { UrlParam } from '../Helper/Url'
import {
  CardTransaction,
  SearchStatus,
  TransactionSearchResponse,
} from '../types'
import {
  TableHeaderColumn,
} from './Table'
import { storeTranxCache } from '../hooks/useTransactionSearch'

const DEFAULT_DISC_DATA_VISIBLE = true
const DEFAULT_CURR_PAGE = 1
const DEFAULT_ITEMS_PER_PAGE = 50
const DEFAULT_SORT_COLUMN = { column: 'id', direction: 'desc' } as const

interface TransactionSectionTitleProps {
  title: string | React.ReactNode
}

function createTranxOnlyUrlParam (
  searchCriteria: TransactionHistorySearchReq,
  cacheKey: string | undefined,
  referrerPath: string,
  isPrinting: boolean = false,
): UrlParam<TransactionHistorySearchReq> {
  return {
    path: '/mu/transactions-only',
    parameters: {
      referrerPath,
      initialStates: { searchCriteria, cacheKey },
    },
    isPrint: isPrinting ? 'y' : 'n',
  }
}

function TransactionSectionTitle ({ title }: TransactionSectionTitleProps): React.ReactElement {
  return (
    <div className="vertical-align-row">
      <GridIcon />
      <span>{title}</span>
    </div>
  )
}

interface TransactionSearchResultProps {
  summary: any
  cardSummaries: any
  transactions: any
  searchStatus: SearchStatus
  searchCriteria?: TransactionHistorySearchReq
  showAll?: boolean
}

export function NoResultsFound () {
  return <div className="no-results-found">
    No Results Found
  </div>
}

interface HeadingConfig {
  allSelected: boolean
  someSelected: boolean
  extraHeadings: string[]
  onHeaderCheckboxToggle: (columnId: string, checked: boolean) => void
}

export function createTableHeadings (
  { allSelected, someSelected, extraHeadings, onHeaderCheckboxToggle }: HeadingConfig
): TableHeaderColumn[] {
  const inputCheckbox = (
    <input type="checkbox" name="select-all" id="select-all" checked={allSelected}
      onChange={(e) => onHeaderCheckboxToggle(e.target.id, e.target.checked)}
      // The indeterminate property highlights if only *some* rows are checked
      // just UX and isn't really necessary, seems nice to display [-] if some are selected
      ref={(el) => { if (el) el.indeterminate = someSelected }}
    />
  )

  const cols: TableHeaderColumn[] = [
    { key: '', component: null },
    { key: 'select_all', component: inputCheckbox, sortable: false },
    { key: 'id', component: 'Transaction ID', sortable: true },
    { key: 'cardNumber', component: 'Card Number', sortable: true },
    { key: 'cardBrand', component: 'Card Brand', sortable: true },
    { key: 'total_amount_with_surcharge', component: 'Total Amount', sortable: true },
    { key: 'approval', component: 'Approval', sortable: true },
    { key: 'avs', component: 'AVS Result', sortable: true },
    { key: 'csc', component: 'CSC Result', sortable: true },
    { key: 'status', component: 'Status', sortable: true },
    { key: 'user_name user', component: 'Name', sortable: true },
    { key: 'invoice', component: 'Invoice', sortable: true },
    { key: 'processMethod', component: 'Method', sortable: true },
    { key: 'user', component: 'User Account', sortable: true },
    { key: 'processed', component: 'When', sortable: true },
    { key: 'settled', component: 'Settled', sortable: true },
    { key: 'eligibility_level', component: 'Eligibility Level', sortable: true },
    { key: 'card_type', component: 'Card Type', sortable: true },
  ]

  const extraCols: TableHeaderColumn[] = extraHeadings.map((value, index) => {
    return { key: `value-${index}`, component: value }
  })

  return [...cols, ...extraCols]
}

function getEffectiveStatus (
  status: SearchStatus, result?: TransactionSearchResponse
): SearchStatus {
  if (status !== undefined) return status
  return result?.transactions ? SearchStatus.Success : SearchStatus.Initial
}

export function isSuccessSearchState (status: SearchStatus, result?: TransactionSearchResponse) {
  return getEffectiveStatus(status, result) === SearchStatus.Success
}

export function isSearchingState (status: SearchStatus, result?: TransactionSearchResponse) {
  return getEffectiveStatus(status, result) === SearchStatus.Searching
}

export function hasNoSearchResults (status: SearchStatus, result?: TransactionSearchResponse) {
  const effStatus = getEffectiveStatus(status, result)
  return effStatus === SearchStatus.NotFound || (
    effStatus === SearchStatus.Success && (
      !result?.transactions || result.transactions.length === 0
    )
  )
}

export function hasSearchError (status: SearchStatus, result?: TransactionSearchResponse) {
  return getEffectiveStatus(status, result) === SearchStatus.Error
}

export function ViewTransactionResult (
  {
    searchCriteria,
    searchStatus,
    showAll = false,
    ...searchResult
  }: TransactionSearchResultProps
) {
  const [openSections, setOpenSections] = useState({
    summary: true,
    transactions: true,
  })
  const searchRecordCount = searchResult.transactions?.length || 0

  const [discDataVisible, setDiscDataVisible] = useState(DEFAULT_DISC_DATA_VISIBLE)
  const [currentPage, setCurrentPage] = useState(DEFAULT_CURR_PAGE)
  const [itemsPerPage, setItemsPerPage] = useState(
    showAll ? searchRecordCount : DEFAULT_ITEMS_PER_PAGE
  )
  const [selectedIds, setSelectedIds] = React.useState<Set<number>>(new Set())
  const [sortConfig, setSortConfig] = React.useState<SortConfig | null>(DEFAULT_SORT_COLUMN)

  const visibleTransactions: CardTransaction[] = React.useMemo(() => {
    if (!searchResult.transactions) {
      return []
    }

    const sortedRecords = getSortedTransactions(searchResult.transactions, sortConfig)
    return getPagedTransactions(sortedRecords, currentPage, itemsPerPage)
  }, [currentPage, itemsPerPage, searchResult.transactions, sortConfig])

  const location = useLocation()

  // Clear selection when the underlying transaction list changes (e.g. new search)
  useEffect(() => {
    setSelectedIds(new Set())
  }, [searchResult.transactions])

  useMountUnmount(() => {
    PubSub.sub(self, Event.DOWNLOAD_TRANSACTION_HISTORY_RESPONSE, async ({
      filename, content,
    }) => {
      await downloadFile(content, filename)
    })

    return () => {
      PubSub.unsub(self)
    }
  })

  // note that the ids are the transaction ids
  const allSelected = visibleTransactions.length > 0 && visibleTransactions.every(t => selectedIds.has(t.id))
  const someSelected = selectedIds.size > 0 && !allSelected

  const extraHeadings = React.useMemo(() => {
    return Array.from(
      new Set(visibleTransactions.flatMap(t => Object.keys(t.discretionary_data || {})))
    )
  }, [visibleTransactions])

  const handleSingleRecordToggle = (recordId: number) => {
    setSelectedIds(prevRecords => {
      const newSelectedRecords = new Set(prevRecords)
      if (newSelectedRecords.has(recordId)) {
        newSelectedRecords.delete(recordId)
      } else {
        newSelectedRecords.add(recordId)
      }
      return newSelectedRecords
    })
  }

  const handleHeaderCheckboxToggle = React.useCallback((columnId: string, checked: boolean) => {
    if (checked) {
      const allIds = visibleTransactions.map(item => item.id)
      setSelectedIds(new Set(allIds))
    } else { // reset and clear
      setSelectedIds(new Set())
    }
  }, [visibleTransactions])

  const handleColumnSorting = (column: string) => {
    setSortConfig((prevSortConfig) => {
      if (prevSortConfig?.column !== column) {
        return { column, direction: 'asc' }
      }

      if (prevSortConfig?.direction === 'asc') {
        return { column, direction: 'desc' } // toggle
      }

      return null
    })
    setCurrentPage(1)
  }

  const headings = React.useMemo(() => {
    return createTableHeadings({
      allSelected,
      someSelected,
      extraHeadings: (discDataVisible ? extraHeadings : []),
      onHeaderCheckboxToggle: handleHeaderCheckboxToggle,
    })
  }, [allSelected, someSelected, extraHeadings, discDataVisible, handleHeaderCheckboxToggle])

  const toggleDiscDataVisibility = () => {
    setDiscDataVisible(previous => !previous)
  }

  const toggleSection = (sectionKey: 'summary' | 'transactions') => {
    setOpenSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }))
  }

  const onPageChange = (page: number) => {
    setCurrentPage(page)
  }

  const onDownload = (isDetail: boolean) => {
    if (searchCriteria) {
      PubSub.pub(Event.DOWNLOAD_TRANSACTION_HISTORY_REQUEST, {
        ...searchCriteria,
        is_detail: isDetail,
      })
    }
  }

  const onShowAll = async () => {
    await viewSearchResults(false)
  }

  const handlePrintAllResults = async () => {
    await viewSearchResults(true)
  }

  // open page with just the search results view
  const viewSearchResults = async (isPrinting: boolean) => {
    const cacheKey = await storeTranxCache(searchResult)

    if (!searchCriteria) {
      throw new Error('Unexpected error caching transaction results')
    }

    const urlParamData = createTranxOnlyUrlParam(
      searchCriteria, cacheKey, location.pathname, isPrinting
    )

    PubSub.pub(Event.OPEN_NEW_TRANX_SEARCH_TAB, urlParamData)
  }

  return (
    <div className="page-container view-transaction-search-results">
      <Accordion
        title="Summary"
        isOpen={openSections.summary}
        onToggle={() => toggleSection('summary')}
        controls={
          <TransactionSectionTitle title={
            <span>
              Search Summary: {searchResult.summary?.totalCount}&nbsp;
              Total Transactions for {formatter.format(searchResult.summary?.totalAmount)}
            </span>
          }/>
        }
      >
        <CardSummaryTable
          cardSummaries={searchResult.cardSummaries}
        />
      </Accordion>

      <Accordion
        title="Transactions"
        isOpen={openSections.transactions}
        onToggle={() => toggleSection('transactions')}
        controls={
          <div className='header-controls-wrapper'>
            <TransactionSectionTitle title="Search Results"/>
            <If isNot={showAll}>
              <TransactionSectionControls
                toggleDiscDataVisibility={toggleDiscDataVisibility}
                discDataVisible={discDataVisible}
                setItemsPerPage={(value: number) => setItemsPerPage(value)}
                onDownload={onDownload}
                onShowAll={onShowAll}
                selectedRecords={selectedIds}
                onSelectAllRecords={handleHeaderCheckboxToggle}
                onPrintAll={handlePrintAllResults}
              />
            </If>
          </div>
        }
      >
        <TransactionTable
          transactions={visibleTransactions}
          showDiscData={discDataVisible}
          selectedRecords={selectedIds}
          onSelectionToggle={handleSingleRecordToggle}
          onSelectAllRecords={handleHeaderCheckboxToggle}
          onSortRecords={handleColumnSorting}
          headings={headings}
          sortConfig={sortConfig}
          showAll={showAll}
        />

        <If is={searchRecordCount > itemsPerPage}>
          <Pagination
            currentPage={currentPage}
            totalItemCount={searchRecordCount}
            onPageChange={onPageChange}
            itemsPerPage={itemsPerPage}
          />
        </If>
      </Accordion>
    </div>
  )
}

export default ViewTransactionResult
